import React, { useMemo, useState } from 'react';

import type { ArticleFinderComponentFields } from '@seuk/cms-api-client';
import { Container } from '@seuk/design-system/atoms';
import { ButtonLink } from '@seuk/design-system/molecules';
import type { CardInterface } from '@seuk/design-system/organisms';
import { MarketingCards } from '@seuk/design-system/organisms';

import type { IArticleCardFields } from '../types';

import ArticlesFinderPagination from './articles-finder-pagination';
import {
  ArticlesFinderContainer,
  BannerImg,
  BannerInfo,
  BannerTitle,
  CategoriesContainer,
  CategoryButton,
  Form,
  SearchClearButton,
  SearchField,
  StyledImage,
} from './styles';

const ARTICLES_PATH = '/energy-insights/articles';
export const PAGE_SIZE = 9;

interface ArticlesFinderProps {
  articleFinder: ArticleFinderComponentFields;
  articles: IArticleCardFields[];
}

const ArticlesFinder: React.FC<ArticlesFinderProps> = ({ articleFinder, articles }) => {
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);

  const filteredArticles = useMemo(() => {
    let result = articles;

    if (searchText.length) {
      const searchValue = searchText.toLowerCase();
      result = result.filter(
        (article) =>
          article.articleTitle.toLowerCase().includes(searchValue) ||
          article.articleSubtitle.toLowerCase().includes(searchValue)
      );
    }

    if (category) {
      result = result.filter((article) => {
        const categorySet = new Set(article.categories);
        return categorySet.has(category);
      });
    }

    return result.filter((article) => !article.presentAsBanner);
  }, [articles, category, searchText]);

  const paginated = useMemo(
    () => filteredArticles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredArticles, page]
  );

  function clearSearchField() {
    setSearchText('');
  }

  function transformMultipleLinesText(text?: string) {
    return text?.split('\n').map((line) => (
      <React.Fragment key={line}>
        {line}
        <br />
      </React.Fragment>
    ));
  }

  const noCategorySelected = useMemo(() => !category.length, [category]);

  const banner = useMemo(
    () =>
      articles.find(
        (article) => !!article.presentAsBanner && (noCategorySelected || article.categories?.includes(category))
      ),
    [articles, category, noCategorySelected]
  );

  const cards = useMemo(
    () => (
      <MarketingCards
        showTitle
        titleAsLink
        columnsAmount={3}
        cards={paginated.map(
          (article) =>
            ({
              image: article.cardImageAsset,
              imageAltText: article.cardImageAltText,
              categories: article.categories,
              title: article.articleTitle,
              date: article.dateVisible ? article.date : null,
              readingTime: article.readingTime,
              description: article.articleSubtitle,
              button: { title: 'Read more', style: 'Secondary', href: `${ARTICLES_PATH}/${article.slug}` },
            } as CardInterface)
        )}
      />
    ),
    [paginated]
  );

  return (
    <ArticlesFinderContainer>
      <Container>
        <Form as="form">
          <SearchField
            id="search"
            label="Search articles"
            placeholder="Search term..."
            value={searchText}
            onChange={({ target }) => setSearchText(target.value)}
          />

          <SearchClearButton type="button" color="secondary" onClick={() => clearSearchField()}>
            Clear
          </SearchClearButton>
        </Form>
        <CategoriesContainer>
          <CategoryButton
            type="button"
            active={noCategorySelected}
            onClick={() => !!category.length && setCategory('')}
          >
            All
          </CategoryButton>

          {articleFinder.categories.map((currCategory) => (
            <CategoryButton
              key={currCategory}
              type="button"
              active={category === currCategory}
              onClick={() => category !== currCategory && setCategory(currCategory)}
            >
              {currCategory}
            </CategoryButton>
          ))}
        </CategoriesContainer>

        {banner && (
          <BannerImg>
            <StyledImage {...banner.cardImageAsset} alt={banner.cardImageAltText} quality="100" />
            <BannerInfo>
              <div>
                <b>{banner.cardLabel}</b>
                <h1>
                  <BannerTitle href={`${ARTICLES_PATH}/${banner.slug}`}>
                    {transformMultipleLinesText(banner.articleTitle)}
                  </BannerTitle>
                </h1>
                <p>{banner.articleSubtitle}</p>
              </div>

              <ButtonLink {...{ color: 'secondary', href: `${ARTICLES_PATH}/${banner.slug}` }}>Read more</ButtonLink>
            </BannerInfo>
          </BannerImg>
        )}

        {cards}

        <ArticlesFinderPagination articlesCounter={articles.length} onPageChange={setPage} />
      </Container>
    </ArticlesFinderContainer>
  );
};
export default ArticlesFinder;
